import hasCorporativeEmailUtil from "@/components/features/center-stage/shared/utils/has-corporative-email";
import { REALMS } from "@/constants/process";
import { EVBASIC_TOKEN_KEY, EVCORE_TOKEN_KEY } from "@/constants/repositories";
import { EMPTY_FEATURE_INFO } from "@/contexts/plan-constants";
import { type Feature, type Plan, PlanStatus, PlanType } from "@/repositories/plan/types";
import type { RealmSliceState } from "@/store/realm-slice";
import { decodeJWT } from "@/utils/auth";
import { getRealmToken, hasToken } from "@/utils/repositories";

export interface FeatureInfo {
  hasFeature: boolean;
  isActive: boolean;
  availableCredits: number;
  usedCredits: number;
  planType?: PlanType;
}

const PLAN_ORDER: Record<PlanType, number> = {
  [PlanType.FREE]: 0,
  [PlanType.PASSIVE_RESEARCH]: 1,
  [PlanType.ACTIVE_RESEARCH]: 2,
};

const LOCALIZED_PLAN_TYPES: Record<string, PlanType> = {
  "ACTIVE RESEARCH": PlanType.ACTIVE_RESEARCH,
  "ACTIVE SEARCH": PlanType.ACTIVE_RESEARCH,
  "BUSCA ATIVA": PlanType.ACTIVE_RESEARCH,
  "BUSCA PASSIVA": PlanType.PASSIVE_RESEARCH,
  "BUSQUEDA ACTIVA": PlanType.ACTIVE_RESEARCH,
  "BUSQUEDA PASIVA": PlanType.PASSIVE_RESEARCH,
  "PASSIVE RESEARCH": PlanType.PASSIVE_RESEARCH,
  "PASSIVE SEARCH": PlanType.PASSIVE_RESEARCH,
  "PESQUISA ATIVA": PlanType.ACTIVE_RESEARCH,
  "PESQUISA PASSIVA": PlanType.PASSIVE_RESEARCH,
};

function normalizePlanName(planName: string): string {
  return planName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
}

function getPlanTypeFromStripeProductName(productName: string): PlanType | undefined {
  return LOCALIZED_PLAN_TYPES[normalizePlanName(productName)];
}

function hasCorporativeEmail(email?: string | null): boolean {
  return hasCorporativeEmailUtil(email || "");
}

function hasActivePlan(plan: Plan | null): boolean {
  return !!plan?.isActive || plan?.status === "ACTIVE";
}

function hasActiveFeatureInPlan(plan: Plan | null, feature: Feature): boolean {
  if (!plan) {
    return false;
  }

  if (!hasActivePlan(plan)) {
    return false;
  }

  const planWithFeature = plan.features.find((f) => f.feature === feature);
  return planWithFeature?.isActive || false;
}

function getFeatureDetailsFromPlan(plan: Plan | undefined, feature: Feature): FeatureInfo {
  if (!plan) {
    return EMPTY_FEATURE_INFO;
  }

  const featureDetails = plan.features.find((f) => f.feature === feature);
  if (!featureDetails) {
    return EMPTY_FEATURE_INFO;
  }

  return {
    hasFeature: true,
    isActive: featureDetails.isActive || false,
    availableCredits: featureDetails.availableCredits || 0,
    usedCredits: featureDetails.usedCredits || 0,
    planType: plan.planType,
  };
}

function canAccessPlanBasedFeatures(
  plan: Plan | null,
  isLoading: boolean,
  isError: boolean
): boolean {
  if (isLoading || isError) {
    return false;
  }

  if (!plan || !hasActivePlan(plan)) {
    return false;
  }

  return true;
}

function canUseFeature(plan: Plan | null, feature: Feature): boolean {
  if (!plan) {
    return false;
  }

  const featureDetails = plan.features.find((f) => f.feature === feature);
  if (!featureDetails?.isActive) {
    return false;
  }

  return (featureDetails.availableCredits || 0) > 0;
}

function isPlanDataLoaded(plan: Plan | null, isLoading: boolean, isError: boolean): boolean {
  return !isLoading && !isError && !!plan;
}

function hasValidAuthToken(): boolean {
  try {
    const evbasicToken = localStorage.getItem(EVBASIC_TOKEN_KEY);
    const evcoreToken = localStorage.getItem(EVCORE_TOKEN_KEY);

    if (!hasToken({ evbasicToken, evcoreToken })) {
      return false;
    }

    const persistedStore = localStorage.getItem("persisted-bound-store");
    if (!persistedStore) {
      return false;
    }

    const realm = JSON.parse(persistedStore)?.state?.realm;
    const realmToken = getRealmToken({ evbasicToken, evcoreToken, realm });
    if (!realmToken) {
      return false;
    }

    const accessToken = JSON.parse(realmToken)?.access_token;
    if (!accessToken) {
      return false;
    }

    const decoded = decodeJWT(accessToken);
    if (!decoded.isValid || !decoded.exp) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return now < decoded.exp;
  } catch {
    return false;
  }
}

function shouldEnablePlanQuery(
  completeProfile: boolean | null | undefined,
  realm?: RealmSliceState["realm"]
): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (!completeProfile && realm !== REALMS.EVBASIC) {
    return false;
  }

  if (!hasValidAuthToken()) {
    return false;
  }

  return true;
}

function hasAvailableCreditsForFeature(plan: Plan | null, feature: Feature): boolean {
  if (!plan) {
    return false;
  }

  const featureDetails = plan.features.find((f) => f.feature === feature);
  if (!featureDetails?.isActive) {
    return false;
  }

  return (featureDetails.availableCredits || 0) > 0;
}

function canAccessCenterStage(plan: Plan | null, isLoading: boolean, isError: boolean): boolean {
  if (isLoading || isError) {
    return false;
  }

  if (!plan || !hasActivePlan(plan)) {
    return false;
  }

  return true;
}

function isFreePlan(plan: Plan | null): boolean {
  return plan?.planType === PlanType.FREE;
}

function hasPaidActivePlan(plan: Plan | null): boolean {
  if (!plan) {
    return false;
  }

  if (plan.planType === PlanType.FREE) {
    return false;
  }

  return plan.status === PlanStatus.ACTIVE;
}

function canShowCancelPlan(plan: Plan | null): boolean {
  return hasPaidActivePlan(plan);
}

function canCancelPlan(plan: Plan | null): boolean {
  if (!canShowCancelPlan(plan)) {
    return false;
  }

  return plan?.cancelAtPeriodEnd !== true;
}

function hasActivePaidPlan(plan: Plan | null): boolean {
  if (!plan || isFreePlan(plan)) {
    return false;
  }

  if (plan.status !== undefined) {
    return plan.status === PlanStatus.ACTIVE;
  }

  return plan.isActive === true;
}

function isUpgrade(current: PlanType | undefined, target: PlanType): boolean {
  if (!current) {
    return true;
  }
  return PLAN_ORDER[target] > PLAN_ORDER[current];
}

function isDowngrade(current: PlanType | undefined, target: PlanType): boolean {
  if (!current) {
    return false;
  }
  return PLAN_ORDER[target] < PLAN_ORDER[current];
}

export {
  hasCorporativeEmail,
  hasActivePlan,
  hasActiveFeatureInPlan,
  getFeatureDetailsFromPlan,
  canAccessPlanBasedFeatures,
  canUseFeature,
  isPlanDataLoaded,
  shouldEnablePlanQuery,
  hasAvailableCreditsForFeature,
  isFreePlan,
  canShowCancelPlan,
  canCancelPlan,
  hasActivePaidPlan,
  canAccessCenterStage,
  isUpgrade,
  isDowngrade,
  getPlanTypeFromStripeProductName,
};
